import { useState, useLayoutEffect, useRef, RefObject } from 'react';

interface UseDeviceGridColumnsOptions {
  marginRight?: number; // Margen derecho del contenedor (ej: 406px en horizontal)
  paddingRight?: number; // Padding derecho del contenedor (en unidades MUI, ej: 1.25)
  gridPaddingX?: number; // Padding horizontal del grid (en unidades MUI, ej: 1.25)
  cardMinWidth?: number; // Ancho mínimo de tarjeta (default: 340px)
  columnGap?: number; // Gap entre columnas (default: 5px)
  containerRef?: RefObject<HTMLElement | null>; // Ref al contenedor para medir su ancho (opcional)
  /** Si se pasa containerRef, al cambiar se vuelve a enganchar medición (habitación, orientación, etc.) — usar valor primitivo estable */
  remeasureKey?: string | number | boolean | null;
}

/** Ancho útil del elemento: clientWidth puede ser 0 con flex/overflow hasta que el layout termina. */
function readElementWidth(el: HTMLElement): number {
  let w = el.clientWidth;
  if (w > 0) {
    return w;
  }
  w = el.offsetWidth;
  if (w > 0) {
    return w;
  }
  w = Math.round(el.getBoundingClientRect().width);
  return w > 0 ? w : 0;
}

/**
 * Hook para calcular el número de columnas para grids de dispositivos/grupos
 * Calcula cuántas columnas caben basándose en el ancho disponible
 * Si se proporciona containerRef, usa el ancho del contenedor en lugar de window.innerWidth
 */
export const useDeviceGridColumns = (options: UseDeviceGridColumnsOptions = {}): number => {
  const {
    marginRight = 0,
    paddingRight = 0,
    gridPaddingX = 0.125, // 1px por defecto (0.125 * 8px)
    cardMinWidth = 340,
    columnGap = 5,
    containerRef,
    remeasureKey,
  } = options;

  const [numColumns, setNumColumns] = useState<number>(1);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useLayoutEffect(() => {
    const useContainer = containerRef !== undefined;

    const calculateColumns = () => {
      let containerWidth: number;

      if (useContainer) {
        const element = containerRef!.current;
        if (!element) {
          return;
        }
        const width = readElementWidth(element);
        if (width <= 0) {
          return;
        }
        containerWidth = width;
      } else {
        containerWidth = window.innerWidth;
      }

      const paddingRightPx = paddingRight * 8;
      const gridPaddingXPx = gridPaddingX * 8;

      let availableWidth: number;
      if (useContainer) {
        availableWidth = containerWidth - marginRight - gridPaddingXPx * 2;
      } else {
        availableWidth = containerWidth - marginRight - paddingRightPx - gridPaddingXPx * 2;
      }

      const columnsThatFit = Math.floor((availableWidth + columnGap) / (cardMinWidth + columnGap));
      const calculatedColumns = Math.max(1, columnsThatFit);
      setNumColumns(calculatedColumns);
    };

    if (!useContainer) {
      calculateColumns();
      const onResize = () => calculateColumns();
      window.addEventListener('resize', onResize);
      return () => {
        window.removeEventListener('resize', onResize);
      };
    }

    let cancelled = false;
    let rafWait: number | null = null;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let observedElement: HTMLElement | null = null;
    let winResizeAttached = false;

    const clearWait = () => {
      if (rafWait !== null) {
        cancelAnimationFrame(rafWait);
        rafWait = null;
      }
      if (retryTimeout !== null) {
        clearTimeout(retryTimeout);
        retryTimeout = null;
      }
    };

    const detachObserverAndResize = () => {
      if (resizeObserverRef.current && observedElement) {
        resizeObserverRef.current.unobserve(observedElement);
        resizeObserverRef.current.disconnect();
      }
      resizeObserverRef.current = null;
      observedElement = null;
    };

    const onWindowResize = () => {
      requestAnimationFrame(() => {
        if (!cancelled) {
          calculateColumns();
        }
      });
    };

    const attachToElement = (el: HTMLElement) => {
      clearWait();
      detachObserverAndResize();

      const calculateWithRetry = (retries = 30, delay = 50) => {
        if (cancelled) {
          return;
        }
        const width = readElementWidth(el);
        if (width > 0 || retries <= 0) {
          requestAnimationFrame(() => {
            if (!cancelled) {
              calculateColumns();
            }
          });
        } else {
          retryTimeout = setTimeout(() => calculateWithRetry(retries - 1, delay), delay);
        }
      };

      calculateWithRetry();

      resizeObserverRef.current = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          if (!cancelled) {
            calculateColumns();
          }
        });
      });
      observedElement = el;
      resizeObserverRef.current.observe(el);

      window.addEventListener('resize', onWindowResize);
      winResizeAttached = true;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) {
            calculateColumns();
          }
        });
      });
    };

    const waitForElement = (maxFrames = 120) => {
      let frames = 0;
      const tick = () => {
        if (cancelled) {
          return;
        }
        const el = containerRef!.current;
        if (el) {
          attachToElement(el);
          return;
        }
        frames += 1;
        if (frames >= maxFrames) {
          return;
        }
        rafWait = requestAnimationFrame(tick);
      };
      rafWait = requestAnimationFrame(tick);
    };

    const elNow = containerRef!.current;
    if (elNow) {
      attachToElement(elNow);
    } else {
      waitForElement();
    }

    return () => {
      cancelled = true;
      clearWait();
      if (winResizeAttached) {
        window.removeEventListener('resize', onWindowResize);
      }
      detachObserverAndResize();
    };
  }, [marginRight, paddingRight, gridPaddingX, cardMinWidth, columnGap, containerRef, remeasureKey]);

  return numColumns;
};
